import type { HTMLAttributeAnchorTarget } from 'react';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { CMSSection } from '@/cms/CMSSection';
import { Flex } from '@/components/Flex';
import { Loader } from '@/components/Loader';
import { SectionHeader } from '@/components/SectionHeader';
import { Typography } from '@/components/Typography';
import { Visibility } from '@/components/Visibility';
import type {
  EyewearAttributesFragment,
  WatchAttributesFragment,
} from '@/graphql/saleor/api';
import { useToggle } from '@/hooks/useToggle';
import type { PlaceholderType } from '@/integrations/contentful';
import { getDropdownValue } from '@/lib/attributes';
import { generateDataTestId } from '@/lib/tests';
import type { Maybe } from '@/lib/types';
import { pdpMessages } from '@/messages/product';
import { DownloadLink } from '@/views/WatchView/components/KeyFeaturesSection/components';

import { Section } from './components';
import WaterResistanceModal from './components/WaterResistanceModal/WaterResistanceModal';
import { isWatchAttributesFragment, useTechnicalDataSections } from './utils';

import { ReactComponent as ArrowDownSvg } from '@/assets/icons/arrowdown-icon.svg';

import * as S from './TechnicalData.styles';

export interface TechnicalDataOpts {
  expandOnLoad?: boolean;
  heading?: string | null;
  learnMoreLinkTarget?: HTMLAttributeAnchorTarget;
  product: Maybe<WatchAttributesFragment | EyewearAttributesFragment>;
  productType: 'watch' | 'eyewear';
  type: PlaceholderType;
  withSectionMargin?: boolean;
}

type TechnicalDataProps = TechnicalDataOpts;

export const TechnicalData = ({
  expandOnLoad = false,
  heading,
  learnMoreLinkTarget,
  product,
  productType,
  type,
  withSectionMargin,
}: TechnicalDataProps) => {
  const { formatMessage } = useIntl();
  const [isOpen, toggle] = useToggle();
  const technicalData = useTechnicalDataSections(productType, product || {});

  const sections = product && technicalData;

  let waterResistanceTextComputed = '';
  if (productType === 'watch' && isWatchAttributesFragment(product)) {
    waterResistanceTextComputed = getDropdownValue(
      product.waterResistanceTextComputed,
    );
  }

  const eyewearDocUrl =
    productType === 'eyewear'
      ? product?.manualUrl?.values[0]?.plainText
      : undefined;

  const loading = product === undefined;

  const [openModal, setOpenModal] = useState(false);
  const [waterResistanceData, setWaterResistanceData] = useState('');
  const [isExpand, setIsExpand] = useState(expandOnLoad);

  const handleModalOpen = (data: string) => {
    setOpenModal(true);
    setWaterResistanceData(data);
  };

  return (
    <CMSSection
      withMargin={withSectionMargin}
      {...generateDataTestId('section', type)}
      data-test-id='TechnicalDataSection'
    >
      <S.Wrapper>
        <S.InnerWrapper showBackground={productType === 'watch'}>
          <Visibility mdHidden>
            <Flex justifyContent='center'>
              {eyewearDocUrl && (
                <DownloadLink
                  href={eyewearDocUrl}
                  label={formatMessage(pdpMessages.downloadEyewearDeclaration)}
                />
              )}
            </Flex>
            <S.StyledAccordion
              data-testid='TechnicalDataAccordion'
              isOpen={isOpen}
              onClick={toggle}
              title={
                <Typography fontWeight='bold' textTransform='uppercase'>
                  {heading ?? ''}
                </Typography>
              }
            >
              {sections?.map(({ attributes, title }, index) => (
                <Section
                  key={title}
                  attributes={attributes}
                  data-testid={`TechnicalDataSection-${index}`}
                  handleModalOpen={handleModalOpen}
                  title={title}
                />
              ))}
            </S.StyledAccordion>
          </Visibility>

          <Visibility mdVisible>
            <Flex flexDirection='column'>
              <Flex flexDirection='row' justifyContent='space-between'>
                {heading && (
                  <SectionHeader
                    data-testid='TechnicalDataHeader'
                    text={heading}
                  />
                )}
                {eyewearDocUrl && (
                  <DownloadLink
                    href={eyewearDocUrl}
                    label={formatMessage(
                      pdpMessages.downloadEyewearDeclaration,
                    )}
                  />
                )}
              </Flex>

              {loading ? (
                <Loader data-testid='TechnicalDataLoader' height={450} />
              ) : (
                <S.SectionWrapper $isExpand={isExpand}>
                  {sections?.map(({ attributes, title }, index) => (
                    <Section
                      key={title}
                      attributes={attributes}
                      data-testid={`TechnicalDataSection-${index}`}
                      handleModalOpen={handleModalOpen}
                      title={title}
                    />
                  ))}
                  {!isExpand && (
                    <S.Overlay>
                      <S.OverlayWrapper>
                        <S.LoadMore
                          label={formatMessage(pdpMessages.loadMore)}
                          onClick={() => setIsExpand(true)}
                          style={{ textDecoration: 'none' }}
                          variant='link'
                          width='auto'
                        >
                          <ArrowDownSvg />
                        </S.LoadMore>
                      </S.OverlayWrapper>
                    </S.Overlay>
                  )}
                </S.SectionWrapper>
              )}
            </Flex>
          </Visibility>
        </S.InnerWrapper>
      </S.Wrapper>
      <WaterResistanceModal
        data-testid='TechnicalDataWaterResistanceModal'
        learnMoreLinkTarget={learnMoreLinkTarget}
        onClose={() => {
          setOpenModal(false);
        }}
        openModal={openModal}
        waterResistanceData={waterResistanceData}
        waterResistanceTextComputed={waterResistanceTextComputed}
      />
    </CMSSection>
  );
};

TechnicalData.displayName = 'TechnicalData';
